import { AddCircleOutline, InfoOutlined } from '@mui/icons-material'
import { Tooltip, useTheme } from '@mui/material'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { invalidationActivityLogCache } from 'src/api/activity-log'
import { IEmptyStatusProps } from 'src/generated-types/empty-status'
import { ITaskFormSchema } from 'src/generated-types/taskFormSchema'
import { MUIThemeColors } from 'src/mui-theme'
import { Padding } from 'src/styles/paddingComponent'
import { getThemeColor } from 'src/utils'
import {
  invalidateSchemaForm,
  useBulkExcelExport,
  useDeleteFormSchemaReportById,
  useGetFormSchemaReports,
} from '../../api/form-schema'
import { invalidateLocalityFrequencyCache } from '../../api/locality-frequency'
import {
  IFormSchema,
  IFormSchemaDto,
  IFormSchemaReport,
  SchemaType,
  formSchemaReportTypes,
} from '../../generated-types/form-schema'
import { useDialogCtx } from '../../hooks/context-hooks'
import { downloadFileLocal } from '../../utils/downloadFile'
import { CommonPageLayout } from '../common/CommonPageLayout'
import FormSchemasDialog from './FormSchemasDialog'
import OverallReportListContainer from './OverallReportListContainer'
import OverallReportTabContainer from './OverallReportTabContainer'
import { useMoveReportDialog } from './useMoveReportDialog'

interface IOverallReport extends IEmptyStatusProps {
  deviationId?: string | EntityId
  localityFrequencyId?: number
  groupId?: string
  customerId: EntityId
  mainComponentId?: string
  type: formSchemaReportTypes
  formInitialValues: object
  disabled?: boolean
  title?: string
  context?: React.Context<any> | null
  usedSchemas?: (schemas: (number | undefined)[] | undefined) => void
  scheduledTaskId?: number
  requiredReports?: ITaskFormSchema[]
  createPermissions: number[]
  deletePermissions: number[]
  height?: number | string
  rowsToFit?: { min?: number; max?: number; fill?: boolean }
  listView?: boolean
  withoutLayout?: boolean
  tableRowHeight?: number
  actionButtonheightAndWidth?: string
  iconWidth?: string
  localityId?: EntityId
  autoHeight?: boolean
  maxHeight?: number | string
  maxVisibleRowCount?: number
  minimalEmptyStatus?: boolean
  useCustomSpinner?: boolean
  setDataLength?: (length: number) => void
  isScheduledTaskIgnored?: boolean
  enableMoveReport?: boolean
}

const OverallReport: React.FC<IOverallReport> = ({
  deviationId,
  type,
  localityFrequencyId,
  groupId,
  formInitialValues,
  mainComponentId,
  disabled,
  title,
  usedSchemas = null,
  scheduledTaskId,
  context,
  requiredReports,
  createPermissions,
  deletePermissions,
  customerId,
  height = 300,
  rowsToFit,
  listView,
  withoutLayout = false,
  tableRowHeight,
  actionButtonheightAndWidth,
  iconWidth,
  maxHeight,
  localityId: localityIdProp,
  autoHeight = false,
  maxVisibleRowCount,
  minimalEmptyStatus = false,
  useCustomSpinner = false,
  setDataLength,
  isScheduledTaskIgnored = false,
  enableMoveReport = false,
  ...rest
}) => {
  const { localityId: localityIdFromParams } = useParams()
  const localityId = localityIdProp ?? localityIdFromParams
  const [bulkUploadLoading, setBulkUploadLoading] = useState(0)
  const { t } = useTranslation(['overall-reports', 'task'])
  const { showConfirmDialog } = useDialogCtx()
  const theme = useTheme()
  const { data, isLoading } = useGetFormSchemaReports(customerId, {
    type,
    localityFrequencyId,
    groupId,
    deviationId: deviationId as string | undefined,
    mainComponentId,
    scheduledTaskId,
    localityId: localityId ? Number(localityId) : undefined,
    schemaType: SchemaType.FormSchema,
  })
  const { mutateAsync: bulkExport } = useBulkExcelExport(customerId as EntityId)
  const { mutateAsync: deleteReport } = useDeleteFormSchemaReportById(customerId as EntityId)
  const { showDialog } = useDialogCtx()

  const draftReports = useMemo<IFormSchemaReport[]>(
    () => data?.flatMap(s => s.formSchemaReports?.filter(r => r.isDraft) ?? []) ?? [],
    [data],
  )

  const requiredReportsList = useMemo(
    () =>
      requiredReports
        ?.map((value, index) => {
          if (index === requiredReports?.length - 1) {
            return value.formSchema.name
          } else {
            return value.formSchema.name + ' | '
          }
        })
        .join(''),
    [requiredReports],
  )

  const handleExport = async (row: IFormSchemaDto) => {
    setBulkUploadLoading(row.id as number)
    await bulkExport({
      formSchemaId: row.id,
      type,
      localityFrequencyId,
      groupId,
      deviationId,
      mainComponentId,
      scheduledTaskId,
      userTimeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    })
      .then(res => {
        downloadFileLocal(res, row?.name)
      })
      .finally(() => {
        setBulkUploadLoading(0)
      })
  }

  const onAdd = () => {
    showDialog(
      FormSchemasDialog,
      {
        componentProps: {
          customerId: customerId,
          localityId: localityId as any,
          property: {
            deviationId: deviationId,
            localityFrequencyId: localityFrequencyId,
            groupId: groupId,
            mainComponentId: mainComponentId,
            type: type,
            scheduledTaskId: scheduledTaskId,
            localityId: localityId != null ? Number(localityId) : undefined,
          },
          formInitialValues: formInitialValues,
          draftReports: draftReports,
        },
        opts: {
          maxWidth: 'lg',
          fullWidth: true,
        },
      },
      undefined,
      true,
    )
  }

  const handleDelete = useCallback((id: number) => {
    showConfirmDialog(
      t('deleteFormSchemaReportTitle', { ns: 'form-schema-report' }),
      t('deleteFormSchemaReportMessage', { ns: 'form-schema-report' }),
      {
        acceptText: t('yes', { ns: 'common' }),
        cancelText: t('no', { ns: 'common' }),
      },
      async pop => {
        await deleteReport(id)
          .then(() => {
            toast.success(t('form-schema-report-deleted', { ns: 'form-schema-report' }))
            invalidateSchemaForm.getFormSchemaReports(customerId as EntityId)
            invalidateLocalityFrequencyCache.getAllLocalityFrequencies(customerId as EntityId, localityId as EntityId)
            invalidationActivityLogCache.getCageActivityLogs()
            pop()
          })
          .catch(() => {
            toast.error(t('error-deleting-form-schema-report', { ns: 'form-schema-report' }))
          })
      },
    )
  }, [])

  const onMove = useMoveReportDialog({
    enabled: enableMoveReport,
    customerId,
    type,
    mainComponentId,
    scheduledTaskId,
    localityId: localityId as EntityId | undefined,
  })

  useEffect(() => {
    const formSchemaIds = data
      ?.filter(s => s?.formSchemaReports != null && s?.formSchemaReports?.length > 0)
      .map(s => s.id)
    if (usedSchemas) {
      usedSchemas(formSchemaIds)
    }
  }, [data])

  const onComplete = (row: IFormSchema | null | undefined, reportId?: number) => {
    showDialog(
      FormSchemasDialog,
      {
        componentProps: {
          customerId: customerId,
          localityId: localityId as any,
          property: {
            deviationId: deviationId,
            localityFrequencyId: localityFrequencyId,
            groupId: groupId,
            mainComponentId: mainComponentId,
            type: type,
            scheduledTaskId: scheduledTaskId,
            localityId: localityId != null ? Number(localityId) : undefined,
          },
          formInitialValues: formInitialValues,
          selectedSchemaForm: row,
          draftReports: draftReports,
          selectedReportId: reportId,
        },
        opts: {
          maxWidth: 'lg',
          fullWidth: true,
        },
      },
      undefined,
      true,
    )
  }

  const fillSection = rowsToFit?.fill === true
  const fillProps = fillSection ? { flex: 1, minHeight: 0 } : {}

  const listContainer = (
    <Padding.p8
      height="100%"
      paddingBottom={'2px'}
      borderRadius={'4px'}
      bgcolor={getThemeColor(theme, MUIThemeColors.white)}
      {...(fillSection ? { display: 'flex', flexDirection: 'column', ...fillProps } : {})}
    >
      {listView ? (
        <OverallReportListContainer
          onComplete={onComplete}
          onMove={onMove}
          disabled={disabled}
          bulkUploadLoading={bulkUploadLoading}
          createPermissions={createPermissions}
          context={context}
          handleExport={handleExport}
          data={data}
          deletePermissions={deletePermissions}
          handleDelete={handleDelete}
          isLoading={isLoading}
          height={height}
          rowsToFit={rowsToFit}
          requiredReports={requiredReports}
          tableRowHeight={tableRowHeight}
          actionButtonheightAndWidth={actionButtonheightAndWidth}
          iconWidth={iconWidth}
          autoHeight={autoHeight}
          maxVisibleRowCount={maxVisibleRowCount}
          minimalEmptyStatus={minimalEmptyStatus}
          useCustomSpinner={useCustomSpinner}
          setDataLength={setDataLength}
          isScheduledTaskIgnored={isScheduledTaskIgnored}
          {...rest}
        />
      ) : (
        <OverallReportTabContainer
          onComplete={onComplete}
          disabled={disabled}
          bulkUploadLoading={bulkUploadLoading}
          createPermissions={createPermissions}
          context={context}
          handleExport={handleExport}
          data={data}
          deletePermissions={deletePermissions}
          handleDelete={handleDelete}
          isLoading={isLoading}
          height={height}
          rowsToFit={rowsToFit}
          requiredReports={requiredReports}
          isScheduledTaskIgnored={isScheduledTaskIgnored}
          minimalEmptyStatus={minimalEmptyStatus}
          {...rest}
        />
      )}
    </Padding.p8>
  )

  if (withoutLayout) {
    return listContainer
  }

  return (
    <CommonPageLayout
      titleSection={{ subtitle: title ? title : t('overall-reports') }}
      titleContainerProps={{ paddingBottom: '12px' }}
      {...fillProps}
      childrenProps={fillSection ? fillProps : undefined}
      topAction={[
        {
          buttonText: t('new-form'),
          buttonIcon: <AddCircleOutline />,
          onClick: onAdd,
          context: context,
          disabled: disabled,
          permissionBinaryValues: createPermissions,
          variant: 'outlined',
          shadowWeight: 0,
        },
      ]}
      warningNote={
        requiredReports != null ? (
          <Tooltip
            sx={{ cursor: 'pointer' }}
            title={`${t('rerquired-reports', { ns: 'task' })} ${requiredReportsList}`}
          >
            <InfoOutlined />
          </Tooltip>
        ) : undefined
      }
    >
      {listContainer}
    </CommonPageLayout>
  )
}

export default OverallReport
